import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileUp, Loader2 } from "lucide-react";
import { api, unwrapObject } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SearchSelect } from "@/components/ui/search-select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export const MAX_PDF_MB = 25;

/**
 * Starts a proposal or quote from a PDF made outside the CRM. The PDF is stored for the deal first, then the record is
 * made from it; a quote also takes the amount (before GST), which becomes the deal's amount once the quote is accepted.
 */
export function UploadDocumentDialog({
  kind,
  open,
  onOpenChange,
  dealId,
  dealName,
}: {
  kind: "proposal" | "quote";
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Fixed deal when opened from a deal page */
  dealId?: string;
  dealName?: string;
}) {
  const navigate = useNavigate();
  const { success, error } = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const isQuote = kind === "quote";
  const [deal, setDeal] = useState(dealId || "");
  const [dealLabel, setDealLabel] = useState(dealName || "");
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState("");
  const [amount, setAmount] = useState("");
  const [gst, setGst] = useState("18");
  const [validUntil, setValidUntil] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDeal(dealId || "");
    setDealLabel(dealName || "");
    setFile(null);
    setTitle("");
    setAmount("");
    setGst("18");
    setValidUntil("");
  }, [open, dealId, dealName]);

  const pick = (f: File | null) => {
    if (!f) return setFile(null);
    if (!/\.pdf$/i.test(f.name)) return error("PDF only", "Choose a PDF file");
    if (f.size > MAX_PDF_MB * 1024 * 1024) return error("File too large", `The PDF must be under ${MAX_PDF_MB} MB`);
    setFile(f);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!deal) return error("Pick a deal", `Choose the deal this ${kind} is for`);
    if (!file) return error("Choose the PDF", `Select the ${kind} PDF to upload`);
    const amt = Number(amount);
    if (isQuote && !(amt > 0)) return error("Enter the amount", "The quote amount (before GST) is what the deal takes when the quote is accepted");
    const gstRate = gst === "" ? 0 : Number(gst);
    if (!(gstRate >= 0 && gstRate <= 28)) return error("Check the GST", "GST must be between 0 and 28%");
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("deal_id", deal);
      fd.append("file", file);
      const up: any = await api.upload("/api/Upload/File", fd);
      const stored = up?.AddtionalData || up?.data || up;
      const body: Record<string, any> = {
        deal_id: deal,
        file_url: stored?.url,
        file_name: stored?.original_name || file.name,
        gst_rate: gstRate,
        ...(amount !== "" ? { amount: amt } : {}),
        ...(validUntil ? { valid_until: validUntil } : {}),
        ...(!isQuote && title.trim() ? { title: title.trim() } : {}),
      };
      const res: any = await api.post(isQuote ? "/api/Quotation/InsertUploaded" : "/api/Proposal/InsertUploaded", body);
      const id = isQuote ? res?.AddtionalData : unwrapObject<{ id: string }>(res).id;
      success(isQuote ? "Quote uploaded" : "Proposal uploaded", "Review it, then send it to the customer");
      onOpenChange(false);
      navigate(`/${isQuote ? "quotes" : "proposals"}/${id}`);
    } catch (err: any) {
      error("Couldn't upload", err.message || "Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Upload a {kind} PDF</DialogTitle>
          <DialogDescription>
            Made the {kind} outside the CRM? Upload it here. It is reviewed, sent and tracked like any other
            {isQuote ? ", and the deal amount follows it once the customer accepts." : "; the customer can accept or decline it on their link."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {!dealId && (
            <div className="space-y-1.5">
              <Label className="text-xs">Deal *</Label>
              <SearchSelect
                value={deal}
                selectedLabel={dealLabel}
                endpoint="/api/Deal/List"
                extraParams={{ status: "OPEN" }}
                placeholder="Search open deals"
                getLabel={(d) => `${d.name}${d.account?.name ? ` · ${d.account.name}` : ""}${d.stage?.name ? ` (${d.stage.name})` : ""}`}
                onChange={(id, row) => {
                  setDeal(id);
                  setDealLabel(row ? `${row.name}${row.account?.name ? ` · ${row.account.name}` : ""}` : "");
                }}
              />
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="text-xs">PDF file *</Label>
            <input ref={fileRef} type="file" accept="application/pdf,.pdf" className="hidden" onChange={(e) => pick(e.target.files?.[0] || null)} />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="w-full flex items-center gap-2 rounded-lg border border-dashed px-3 py-3 text-left text-sm hover:bg-muted/50"
            >
              <FileUp className="size-4 text-primary shrink-0" />
              <span className="truncate">{file ? `${file.name} · ${(file.size / 1024 / 1024).toFixed(1)} MB` : `Choose a PDF (up to ${MAX_PDF_MB} MB)`}</span>
            </button>
          </div>
          {!isQuote && (
            <div className="space-y-1.5">
              <Label className="text-xs">Title</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Defaults to the deal name" maxLength={200} />
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Amount before GST (₹){isQuote ? " *" : ""}</Label>
              <Input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="e.g. 250000" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">GST %</Label>
              <Input type="number" min="0" max="28" step="0.01" value={gst} onChange={(e) => setGst(e.target.value)} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Valid until</Label>
            <Input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />
            <p className="text-[11px] text-muted-foreground">Leave empty to use the default validity.</p>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving || !deal || !file}>
              {saving && <Loader2 className="size-4 animate-spin mr-2" />}
              Upload
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
